import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom'
import { lazy, Suspense, useEffect } from 'react'
import MainLayout from './components/Layout/MainLayout'
import { useAuthStore } from './stores/authStore'

const Dashboard = lazy(() => import('./pages/Dashboard'))
const Workspace = lazy(() => import('./pages/Workspace'))
const Insights = lazy(() => import('./pages/Insights'))
const Reports = lazy(() => import('./pages/Reports'))
const QualityCheck = lazy(() => import('./pages/QualityCheck'))
const AIChat = lazy(() => import('./pages/AIChat'))
const Settings = lazy(() => import('./pages/Settings'))
const AnalysisReview = lazy(() => import('./pages/AnalysisReview'))
const Login = lazy(() => import('./pages/Login'))
const SharedConversation = lazy(() => import('./pages/SharedConversation'))

const PageFallback = () => (
  <div style={{ display: 'grid', gap: 16 }}>
    <div style={{ height: 32, width: 220, borderRadius: 4, background: '#f0f2f5' }} />
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 16 }}>
      {Array.from({ length: 4 }).map((_, index) => (
        <div key={index} style={{ height: 96, borderRadius: 6, background: '#f5f5f5' }} />
      ))}
    </div>
    <div style={{ height: 360, borderRadius: 6, background: '#f5f5f5' }} />
  </div>
)

const ROUTE_PERMISSION_MAP: Record<string, string> = {
  '/': 'workspace:view',
  '/dashboard': 'dashboard:view',
  '/analysis-review': 'analysis_review:view',
  '/insights': 'insights:view',
  '/reports': 'reports:view',
  '/quality': 'quality:view',
  '/chat': 'chat:view',
  '/settings': 'settings:account',
}

const DEFAULT_ROUTE_BY_PERMISSION: Record<string, string> = {
  'workspace:view': '/',
  'dashboard:view': '/dashboard',
  'analysis_review:view': '/analysis-review',
  'insights:view': '/insights',
  'reports:view': '/reports',
  'quality:view': '/quality',
  'chat:view': '/chat',
  'settings:account': '/settings',
}

const ProtectedRoute = ({ path, children }: { path: string; children: React.ReactNode }) => {
  const { hasPermission } = useAuthStore()
  const permission = ROUTE_PERMISSION_MAP[path]
  if (permission && !hasPermission(permission)) {
    const { user } = useAuthStore.getState()
    if (user?.roleName === 'super_admin') {
      return <Navigate to="/" replace />
    }
    for (const [perm, route] of Object.entries(DEFAULT_ROUTE_BY_PERMISSION)) {
      if (hasPermission(perm)) {
        return <Navigate to={route} replace />
      }
    }
    return <Navigate to="/chat" replace />
  }
  return <>{children}</>
}

const AppLayout = () => (
  <MainLayout>
    <Suspense fallback={<PageFallback />}>
      <Outlet />
    </Suspense>
  </MainLayout>
)

function App() {
  const { isAuthenticated, checkAuth } = useAuthStore()

  useEffect(() => {
    if (isAuthenticated) {
      checkAuth()
    }
  }, [])

  if (!isAuthenticated) {
    return (
      <BrowserRouter>
        <Suspense fallback={<PageFallback />}>
          <Routes>
            <Route path="/shared/conversations/:token" element={<SharedConversation />} />
            <Route path="/login" element={<Login />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    )
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/shared/conversations/:token" element={
          <Suspense fallback={<PageFallback />}>
            <SharedConversation />
          </Suspense>
        } />
        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route element={<AppLayout />}>
          <Route
            index
            element={
              <ProtectedRoute path="/">
                <Workspace />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute path="/dashboard">
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/insights"
            element={
              <ProtectedRoute path="/insights">
                <Insights />
              </ProtectedRoute>
            }
          />
          <Route
            path="/analysis-review"
            element={
              <ProtectedRoute path="/analysis-review">
                <AnalysisReview />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports"
            element={
              <ProtectedRoute path="/reports">
                <Reports />
              </ProtectedRoute>
            }
          />
          <Route
            path="/quality"
            element={
              <ProtectedRoute path="/quality">
                <QualityCheck />
              </ProtectedRoute>
            }
          />
          <Route
            path="/chat"
            element={
              <ProtectedRoute path="/chat">
                <AIChat />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute path="/settings">
                <Settings />
              </ProtectedRoute>
            }
          />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  )
}

export default App
